import type { Logger } from 'loglevel';
import { ENV } from '../config/env';

export type EdiDsnBilansServiceResult<T> =
  | { success: true; data: T; contentType: string | null; filename?: string | null }
  | { success: false; error: string };

export interface BilanDocumentContent {
  content: string;
}

export abstract class EdiDsnBilansBaseService {
  protected readonly apiBaseUrl = ENV.BACK_EDI_URL;

  constructor(
    private readonly getAccessToken: () => Promise<string>,
    protected readonly logger: Logger,
  ) {}

  protected async fetchGetQuery(
    fetchUrl: string,
    method: string,
    signal: AbortSignal | null = null,
    extraHeaders: Array<[string, string]> = [],
  ): Promise<Response> {
    const accessToken = await this.getAccessToken();
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };

    for (const [key, value] of extraHeaders) {
      headers[key] = value;
    }

    const response = await fetch(fetchUrl, {
      method: 'GET',
      headers,
      signal,
    });

    this.logger.info(`Response ${method} fetch:`, { status: response.status });

    if (!response.ok) {
      this.logger.warn(`Error ${method} fetch:`, {
        status: response.status,
        statusText: response.statusText,
      });

      throw new Error(this.getUserFriendlyErrorMessage(response.status, method));
    }

    return response;
  }

  protected getUserFriendlyErrorMessage(status: number, method: string): string {
    switch (status) {
      case 401:
        return `Session expir\u00e9e. Veuillez vous reconnecter. [${method} (code: ${status})]`;
      case 403:
        return `Vous n'avez pas les droits pour acc\u00e9der \u00e0 cette ressource. [${method} (code: ${status})]`;
      case 404:
        return `Le bilan demand\u00e9 est introuvable. [${method} (code: ${status})]`;
      case 500:
      case 502:
      case 503:
        return `Le service est temporairement indisponible. Veuillez r\u00e9essayer. [${method} (code: ${status})]`;
      default:
        return `Erreur lors de la r\u00e9cup\u00e9ration du ${method} (code: ${status})`;
    }
  }

  protected validateInstanceId(
    id: string,
    paramName = 'bilan',
  ): EdiDsnBilansServiceResult<never> | null {
    if (!id?.trim()) {
      return {
        success: false,
        error: `L'identifiant du ${paramName} est requis`,
      };
    }

    return null;
  }
}
